import { isMetricUnit } from 'engineering/components/engineeringProjectDocuments/utils/unitTypes';
import { metersToInches } from 'maths';
import * as PIXI from 'pixi.js';
import { calcDistanceOfTwoPoints } from '__common/calculations/distanceBetweenTwoPoints';
import { inchesToCms,inchesToFeets,inchesToMeters } from '__common/calculations/unitConversions';
import { isAscender } from '__common/constants/products';
import { calculateEdgeDimensionOnStage } from './advanceRoofSelecting';
import { dispatch } from '__common/store';
import { DELETE_MEASUREMENT, RESET_CURRENT_MEASUREMENT } from '__editor/googleMapsRoofsSelector/components/measurementTool/measurementToolActions';

function getLineWidth(scale: number) {
  return 3 / scale;
}

export function measurementToolModel(startingPoint: pixelPoint, endingPoint: pixelPoint, scale: number, productId: number, inputUnit?: number, panelEditorActive: boolean = false, metersPerPixel: number = 0, index?: number) {

  if (!startingPoint || !endingPoint) {
    return new PIXI.Graphics();
  }

  const line = new PIXI.Graphics();
  line.lineStyle(getLineWidth(scale), 0x0083c2, 1);
  line.moveTo(startingPoint.x, startingPoint.y);
  line.lineTo(endingPoint.x, endingPoint.y);

  const lengthDimension = panelEditorActive ? calculateEdgeDimensionOnPanelEditor(startingPoint, endingPoint, isAscender(productId) || isMetricUnit(inputUnit), metersPerPixel)
  : calculateEdgeDimensionOnStage(startingPoint, endingPoint, isAscender(productId) || isMetricUnit(inputUnit));
  const lengthDimensionObj = createLengthDimensionObj(lengthDimension, scale, index);
  
  lengthDimensionObj.zIndex = 99999;
  line.zIndex = 99999;
  line.addChild(lengthDimensionObj);

  return line;
}

function calculateEdgeDimensionOnPanelEditor(startingPoint: pixelPoint, endingPoint: pixelPoint, metricUnits: boolean, metersPerPixel: number) : edgeDimension{
  const distanceinInches = metersToInches(calcDistanceOfTwoPoints(startingPoint, endingPoint) * metersPerPixel);
  const distance = metricUnits ? inchesToMeters(distanceinInches).toFixed(2) + " m" : inchesToFeets(distanceinInches).toFixed(2)  + "ft";
  const angleInRadians = Math.atan2(endingPoint.y - startingPoint.y, endingPoint.x - startingPoint.x);
  const midPoint = {
    x : (startingPoint.x + endingPoint.x)/2,
    y : (startingPoint.y + endingPoint.y)/2
  };
  const edgeDimension = {
    length: distance,
    midPoint,
    rotation : angleInRadians
  }
  return edgeDimension; 

}

function createLengthDimensionObj(lengthDimension: edgeDimension, scale, index?: number){
  let {
    midPoint : {
      x,
      y
    },
    length,
    rotation
  } = lengthDimension
  
  // creating text object
  const text: PIXI.Text = new PIXI.Text(length, {
    fontSize: '16px', fill: 0xFFFFFF, fontWeight: "700"
  });
  const width = (text.width / 2) / scale;
  const height = (text.height / 2) / scale;
  let xOffset = (width * Math.cos(rotation) - (height * Math.sin(rotation)));
  let yOffset = (width * Math.sin(rotation) + (height * Math.cos(rotation)));
  if (Math.PI/2  < rotation || rotation < -Math.PI/2){
    xOffset = -xOffset;
    yOffset = -yOffset;
    rotation += Math.PI;
  }
  text.x = x - xOffset;
  text.y = y - yOffset;
  text.width /= scale;
  text.height /= scale;

  // creating background obj
  const textBG = new PIXI.Graphics();
  textBG.lineStyle(getLineWidth(scale), 0x0083c2, 1);
  textBG.beginFill(0x000000)
  xOffset = 10 / scale;
  yOffset = 5 / scale;

  let totalWidth = text.width;
  
  // Custom drawn trash bin icon
  const deleteBin = new PIXI.Graphics();
  const deleteLabel = new PIXI.Text('DELETE', {
    fontSize: '14px', fill: 0xFFFFFF, fontWeight: "600"
  });
  const deleteLabelBG = new PIXI.Graphics();
  const binWidth = 16 / scale;
  const binHeight = 16 / scale;
  if (index !== undefined) {
    const drawBin = (color: number) => {
      deleteBin.clear();
      deleteBin.beginFill(color);
      deleteBin.lineStyle(1 / scale, color, 1);
      // Bin body
      deleteBin.moveTo(binWidth * 0.2, binHeight * 0.2);
      deleteBin.lineTo(binWidth * 0.8, binHeight * 0.2);
      deleteBin.lineTo(binWidth * 0.75, binHeight);
      deleteBin.lineTo(binWidth * 0.25, binHeight);
      deleteBin.lineTo(binWidth * 0.2, binHeight * 0.2);
      deleteBin.endFill();

      // Lid and handle
      deleteBin.lineStyle(1.5 / scale, color, 1);
      // Lid line
      deleteBin.moveTo(0, binHeight * 0.2);
      deleteBin.lineTo(binWidth, binHeight * 0.2);
      // Handle
      deleteBin.moveTo(binWidth * 0.35, binHeight * 0.2);
      deleteBin.lineTo(binWidth * 0.35, 0);
      deleteBin.lineTo(binWidth * 0.65, 0);
      deleteBin.lineTo(binWidth * 0.65, binHeight * 0.2);
    };
    drawBin(0xFF0000);
    
    deleteBin.visible = false; // Hidden by default
    deleteBin.x = text.x + text.width + xOffset/2 - binWidth/2;
    deleteBin.y = text.y - yOffset/2 - binHeight/2;

    deleteLabel.scale.set(1 / scale);
    deleteLabel.visible = false;
    deleteLabel.x = deleteBin.x + (binWidth - deleteLabel.width) / 2;
    deleteLabel.y = deleteBin.y - deleteLabel.height - 4/scale;

    deleteLabelBG.beginFill(0x000000, 0.7);
    deleteLabelBG.drawRect(
      deleteLabel.x - 4/scale,
      deleteLabel.y - 2/scale,
      deleteLabel.width + 8/scale,
      deleteLabel.height + 4/scale
    );
    deleteLabelBG.endFill();
    deleteLabelBG.visible = false;
  }
  
  textBG.interactive = true;
  textBG.buttonMode = true;
  textBG.name = 'measurementToolInteractive';
  
  if (index !== undefined) {
    textBG.on('mouseover', () => {
      deleteBin.visible = true; // Show bin on hover
      deleteLabel.visible = true; // Show label on hover
      deleteLabelBG.visible = true;
      textBG.clear();
      textBG.lineStyle(getLineWidth(scale), 0x0083c2, 1);
      textBG.beginFill(0x000000);
      textBG.drawRect(
        text.position.x - xOffset/2,
        text.position.y - yOffset/2,
        totalWidth + xOffset,
        text.height + yOffset,
      );
    });

    textBG.on('mouseout', () => {
      textBG.clear();
      deleteBin.visible = false; 
      deleteLabel.visible = false;
      deleteLabelBG.visible = false;
      textBG.lineStyle(getLineWidth(scale), 0x0083c2, 1);
      textBG.beginFill(0x000000);
      textBG.drawRect(
        text.position.x - xOffset/2,
        text.position.y - yOffset/2,
        totalWidth + xOffset,
        text.height + yOffset,
      );
    });
  }

  textBG.on('click', (e) => {
    e.stopPropagation();
    if (index !== undefined) {
      dispatch(DELETE_MEASUREMENT(index));
    } else {
      dispatch(RESET_CURRENT_MEASUREMENT());
    }
    window.dispatchEvent(new CustomEvent('REFRESH_MEASUREMENT_TOOL'));
  });

  textBG.drawRect(
    text.position.x - xOffset/2,
    text.position.y - yOffset/2,
    totalWidth + xOffset,
    text.height + yOffset,
  );
  textBG.position = text.position;
  textBG.pivot = text.position;
  textBG.rotation = rotation;

  textBG.addChild(text);
  textBG.addChild(deleteBin);
  textBG.addChild(deleteLabelBG);
  textBG.addChild(deleteLabel);
  return textBG
}
